import React, { useState, useEffect, useCallback } from 'react';
import { Table, Form, InputGroup, Container, Row, Col, Button, Modal, Badge, Toast } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShoppingCart, faPlus, faFileExcel, faSortUp, faSortDown, faSort, faCheck, faBoxes } from '@fortawesome/free-solid-svg-icons';
import { getInventoryByLot, API_URL } from '../services/api';
import ShoppingCart from './ShoppingCart';
import Checkout from './Checkout';
import './Inventory.css';
import '../styles/InfiniteTable.css';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import PageHeader from './PageHeader';

console.log('API_URL:', API_URL);

function Inventory() {
  const [inventory, setInventory] = useState([]);
  const [filteredInventory, setFilteredInventory] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [cart, setCart] = useState(() => {
    // Initialize cart from sessionStorage
    const savedCart = sessionStorage.getItem('cart');
    return savedCart ? JSON.parse(savedCart) : [];
  });
  const [showCart, setShowCart] = useState(false);
  const [showQuantityModal, setShowQuantityModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [quantityToAdd, setQuantityToAdd] = useState(1);
  const [showCheckout, setShowCheckout] = useState(false);
  const [customerId, setCustomerId] = useState(null); // You'll need to set this when the user logs in or selects a customer
  const { user } = useAuth();
  const navigate = useNavigate();
  const [pendingOrdersCache, setPendingOrdersCache] = useState({});
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  useEffect(() => {
    if (!user) {
      navigate('/login');
    } else {
      fetchInventory();
    }
  }, [user, navigate]);

  // Save cart to sessionStorage whenever it changes
  useEffect(() => {
    sessionStorage.setItem('cart', JSON.stringify(cart));
  }, [cart]);

  // Validate cart quantities against inventory when inventory loads
  useEffect(() => {
    if (inventory.length > 0 && cart.length > 0) {
      let cartNeedsUpdate = false;
      const updatedCart = cart.map(cartItem => {
        const inventoryItem = inventory.find(item => item.idLevels === cartItem.idLevels);
        if (!inventoryItem) {
          cartNeedsUpdate = true;
          return null; // Remove item if it no longer exists in inventory
        }
        if (cartItem.quantity > Number(inventoryItem.availQty)) {
          cartNeedsUpdate = true;
          return {
            ...cartItem,
            quantity: Number(inventoryItem.availQty) // Adjust quantity to available amount
          };
        }
        return cartItem;
      }).filter(Boolean); // Remove null items

      if (cartNeedsUpdate) {
        setCart(updatedCart);
        setToastMessage('Cart has been updated to reflect available quantities');
        setShowToast(true);
      }
    }
  }, [inventory]);

  const fetchInventory = async () => {
    try {
      const data = await getInventoryByLot();
      setInventory(data);
    } catch (error) {
      console.error('Error fetching inventory:', error);
      // Handle error (e.g., show error message to user)
    }
  };

  const filterInventory = useCallback((inventory) => {
    return inventory.filter(item =>
      Object.values(item).some(value =>
        value && value.toString().toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [searchTerm]);

  const sortInventory = useCallback((data) => {
    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Handle null/undefined values
      if (!aValue) aValue = '';
      if (!bValue) bValue = '';

      // Convert to numbers for numeric columns
      if (['onHand', 'availQty', 'PPU'].includes(sortConfig.key)) {
        aValue = Number(aValue);
        bValue = Number(bValue);
      }

      // Handle dates
      if (['recDate', 'expDate'].includes(sortConfig.key)) {
        aValue = new Date(aValue || 0);
        bValue = new Date(bValue || 0);
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [sortConfig]);

  useEffect(() => {
    let result = [...inventory];
    
    // Apply search filter
    if (searchTerm) {
      result = result.filter(item =>
        Object.values(item).some(value =>
          value && value.toString().toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }
    
    // Apply sorting
    result = sortInventory(result);
    
    setFilteredInventory(result);
  }, [inventory, searchTerm, sortInventory]);

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return faSort;
    }
    return sortConfig.direction === 'asc' ? faSortUp : faSortDown;
  };

  const getCartItemQuantity = (idLevels) => {
    if (!idLevels) return 0;
    const cartItem = cart.find(item => item.idLevels === idLevels);
    return cartItem ? cartItem.quantity : 0;
  };

  const getTrueAvailableQty = (item) => {
    if (!item || !item.idLevels || !item.availQty) return 0;
    const cartQty = getCartItemQuantity(item.idLevels);
    return Math.max(0, Number(item.availQty) - cartQty);
  };

  const handleAddToCart = (item) => {
    const trueAvailable = getTrueAvailableQty(item);
    
    if (trueAvailable <= 0) {
      setToastMessage('No more items available');
      setShowToast(true);
      return;
    }

    if (trueAvailable === 1) {
      const existingItem = cart.find(cartItem => cartItem.idLevels === item.idLevels);
      
      if (existingItem) {
        setCart(cart.map(cartItem =>
          cartItem.idLevels === item.idLevels
            ? { ...cartItem, quantity: existingItem.quantity + 1 }
            : cartItem
        ));
      } else {
        setCart([...cart, {
          idLevels: item.idLevels,
          itemName: item.itemName,
          quantity: 1,
          idLoc: item.idLoc,
          dimLen: item.dimLen,
          dimWdt: item.dimWdt,
          dimHgt: item.dimHgt,
          weight: item.weight,
          notes: item.notes,
          availQty: item.availQty
        }]);
      }
      
      setToastMessage(`Added ${item.itemName} to cart`);
      setShowToast(true);
    } else {
      setSelectedItem(item);
      setQuantityToAdd(1);
      setShowQuantityModal(true);
    }
  };

  const handleQuantitySubmit = () => {
    if (!selectedItem) return;
    
    const trueAvailable = getTrueAvailableQty(selectedItem);
    
    if (trueAvailable <= 0) {
      setToastMessage('No more items available');
      setShowToast(true);
      setShowQuantityModal(false);
      return;
    }

    const quantityToAddValidated = Math.min(quantityToAdd, trueAvailable);
    
    const existingItem = cart.find(cartItem => cartItem.idLevels === selectedItem.idLevels);
    if (existingItem) {
      setCart(cart.map(cartItem =>
        cartItem.idLevels === selectedItem.idLevels
          ? { ...cartItem, quantity: existingItem.quantity + quantityToAddValidated }
          : cartItem
      ));
    } else {
      setCart([...cart, {
        idLevels: selectedItem.idLevels,
        itemName: selectedItem.itemName,
        quantity: quantityToAddValidated,
        idLoc: selectedItem.idLoc,
        dimLen: selectedItem.dimLen,
        dimWdt: selectedItem.dimWdt,
        dimHgt: selectedItem.dimHgt,
        weight: selectedItem.weight,
        notes: selectedItem.notes
      }]);
    }

    setToastMessage(`Added ${quantityToAddValidated} ${selectedItem.itemName}${quantityToAddValidated > 1 ? 's' : ''} to cart`);
    setShowToast(true);
    setShowQuantityModal(false);
  };

  const handleAddAll = () => {
    if (!selectedItem) return;
    
    const trueAvailable = getTrueAvailableQty(selectedItem);
    
    if (trueAvailable <= 0) {
      setToastMessage('No more items available');
      setShowToast(true);
      setShowQuantityModal(false);
      return;
    }
    
    const existingItem = cart.find(cartItem => cartItem.idLevels === selectedItem.idLevels);
    
    if (existingItem) {
      setCart(cart.map(cartItem =>
        cartItem.idLevels === selectedItem.idLevels
          ? { ...cartItem, quantity: existingItem.quantity + trueAvailable }
          : cartItem
      ));
    } else {
      setCart([...cart, {
        idLevels: selectedItem.idLevels,
        itemName: selectedItem.itemName,
        quantity: trueAvailable,
        idLoc: selectedItem.idLoc,
        dimLen: selectedItem.dimLen,
        dimWdt: selectedItem.dimWdt,
        dimHgt: selectedItem.dimHgt,
        weight: selectedItem.weight,
        notes: selectedItem.notes
      }]);
    }

    setToastMessage(`Added all ${trueAvailable} ${selectedItem.itemName}${trueAvailable > 1 ? 's' : ''} to cart`);
    setShowToast(true);
    setShowQuantityModal(false);
  };

  const handleCheckoutComplete = async (checkoutData) => {
    try {
      const now = new Date();
      const mysqlDatetime = now.toISOString().slice(0, 19).replace('T', ' ');

      const user = JSON.parse(sessionStorage.getItem('user'));
      if (!user?.customerId) {
        throw new Error('No customer ID found in session');
      }

      const selectedRateId = checkoutData.selectedRate;
      const shippingRates = JSON.parse(sessionStorage.getItem('shippingRates') || '[]');
      const selectedRate = shippingRates.find(rate => rate.rate_id === selectedRateId);

      const orderData = {
        header: {
          idCustomer: user.customerId,
          idClient: user.idClient,
          transDate: mysqlDatetime,
          transtype: 4,
          sGuid: window.crypto.randomUUID(),
          CustomerRef: checkoutData.customerReference || '',
          delName: checkoutData.shippingDetails?.name || '',
          delAddress: checkoutData.shippingDetails?.address1 || '',
          delCity: checkoutData.shippingDetails?.city || '',
          delState: checkoutData.shippingDetails?.state || '',
          delZip: checkoutData.shippingDetails?.zipCode || '',
          delPhone: checkoutData.shippingDetails?.phone || '',
          delNotes: checkoutData.shippingMethod === 'hold' ? checkoutData.holdInstructions : '',
          pickName: user.homeWarehouse?.name || '',
          pickAddress: user.homeWarehouse?.address?.street || '',
          pickCity: user.homeWarehouse?.address?.city || '',
          pickState: user.homeWarehouse?.address?.state || '',
          pickZip: user.homeWarehouse?.address?.zip || '',
          pickPhone: user.homeWarehouse?.address?.phone || '',
          status: 0,
          shipStat: 0,
          createDate: mysqlDatetime,
          shipping_service_code: selectedRate?.service_code || '',
          shipping_carrier_id: selectedRate?.carrier_id || '',
          shipping_rate_id: selectedRate?.rate_id || '',
          shipping_rate_amount: selectedRate?.shipping_amount?.amount || 0,
          shipping_service_type: selectedRate?.service_type || '',
          shipping_estimated_days: selectedRate?.delivery_days || 0,
          carrier: selectedRate?.carrier_friendly_name || '',
          shipping_id: selectedRate?.shipment_id || null
        },
        items: cart.map(item => ({
          recTrans: item.idLevels,
          transQty: item.quantity,
          notes: item.notes || '',
          idLoc: item.idLoc || null,
          dimLen: item.dimLen || null,
          dimWdt: item.dimWdt || null,
          dimHgt: item.dimHgt || null,
          weight: item.weight || null,
          shipping_id: selectedRate?.shipment_id || null
        }))
      };

      const response = await fetch(`${API_URL}/api/inventory/pick-order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(orderData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Server error response:', errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('Pick order created successfully:', result);

      if (result.success) {
        setCart([]);
        sessionStorage.removeItem('cart');
        navigate('/orders');
      } else {
        throw new Error(result.message || 'Failed to create pick order');
      }

    } catch (error) {
      console.error('Error creating pick order:', error);
      alert('Failed to create pick order. Please try again.');
      throw error;
    }
  };

  const fetchPendingOrders = async (lotId) => {
    try {
      const response = await fetch(`${API_URL}/api/inventory/pending-orders/${lotId}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include'
      });

      const text = await response.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch (e) {
        console.error('Failed to parse response as JSON:', text.substring(0, 100));
        throw e;
      }

      setPendingOrdersCache(prev => ({
        ...prev,
        [lotId]: data
      }));
    } catch (error) {
      console.error('Error in fetchPendingOrders:', error);
      setPendingOrdersCache(prev => ({
        ...prev,
        [lotId]: []
      }));
    }
  };

  const handleExportToExcel = async () => {
    try {
      const searchParam = searchTerm ? `?search=${encodeURIComponent(searchTerm)}` : '';
      const response = await fetch(`${API_URL}/api/inventory/export${searchParam}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        }
      });

      if (!response.ok) {
        if (response.status === 404) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || 'No inventory data available for export');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Get the filename from the response headers
      const contentDisposition = response.headers.get('Content-Disposition');
      const filename = contentDisposition 
        ? contentDisposition.split('filename=')[1]?.replace(/"/g, '') 
        : 'inventory_export.xlsx';

      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setToastMessage('Excel export downloaded successfully');
      setShowToast(true);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      setToastMessage('Failed to export to Excel. Please try again.');
      setShowToast(true);
    }
  };

  if (showCheckout) {
    return (
      <Checkout 
        cart={cart} 
        onCheckoutComplete={handleCheckoutComplete}
        onCancel={() => setShowCheckout(false)}
      />
    );
  }

  const renderInventoryTable = () => {
    const columns = [
      { key: 'idLevels', label: 'Lot ID', sortable: true, align: 'right' },
      { key: 'itemNumber', label: 'Item Number', sortable: true, align: 'left' },
      { key: 'itemName', label: 'Item Name', sortable: true, align: 'left' },
      { key: 'notes', label: 'Notes', sortable: false, align: 'left' },
      { key: 'recDate', label: 'Received Date', sortable: true, align: 'right' },
      { key: 'expDate', label: 'Expiration Date', sortable: true, align: 'right' },
      { key: 'PPU', label: 'PPU', sortable: true, align: 'right' },
      { key: 'onHand', label: 'On Hand', sortable: true, align: 'right' },
      { key: 'availQty', label: 'Available', sortable: true, align: 'right' },
      { key: 'actions', label: 'Actions', sortable: false, align: 'center' }
    ];

    return (
      <table style={{ 
        width: '100%',
        borderCollapse: 'collapse',
        marginBottom: 0,
        backgroundColor: 'white'
      }}>
        <thead>
          <tr>
            {columns.map(column => (
              <th 
                key={column.key}
                className={column.sortable ? `sortable-header ${sortConfig.key === column.key ? 'active' : ''}` : ''}
                onClick={() => column.sortable && requestSort(column.key)}
                style={{ 
                  position: 'sticky', 
                  top: 0, 
                  zIndex: 999,
                  background: 'linear-gradient(135deg, #4a90e2, #357abd)',
                  color: 'white',
                  borderBottom: '3px solid #2968a3',
                  boxShadow: '0 2px 8px rgba(74, 144, 226, 0.3)',
                  padding: '8px 12px',
                  fontWeight: 600,
                  fontSize: '0.8rem',
                  textAlign: column.align,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  cursor: column.sortable ? 'pointer' : 'default',
                  userSelect: 'none',
                  border: '1px solid #2968a3',
                  lineHeight: '1.2'
                }}
              >
                <div style={{ 
                  display: 'flex', 
                  justifyContent: column.align === 'right' ? 'flex-end' : column.align === 'center' ? 'center' : 'flex-start',
                  alignItems: 'center'
                }}>
                  <span>{column.label}</span>
                  {column.sortable && (
                    <FontAwesomeIcon 
                      icon={getSortIcon(column.key)} 
                      style={{ marginLeft: '8px', opacity: 0.9, color: 'white' }}
                    />
                  )}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {filteredInventory.map((item, idx) => (
            <tr key={`lot-${item.idLevels}`} style={{ 
              backgroundColor: idx % 2 === 0 ? '#ffffff' : '#f8f9fa'
            }}>
              <td style={{ padding: '6px 12px', border: '1px solid #dee2e6', textAlign: 'right', fontSize: '0.85rem', lineHeight: '1.2' }}>{item.idLevels}</td>
              <td style={{ padding: '6px 12px', border: '1px solid #dee2e6', textAlign: 'left', fontSize: '0.85rem', lineHeight: '1.2' }}>
                <strong>{item.itemNumber.trim()}</strong>
              </td>
              <td style={{ padding: '6px 12px', border: '1px solid #dee2e6', textAlign: 'left', fontSize: '0.85rem', lineHeight: '1.2' }}>{item.itemName}</td>
              <td style={{ padding: '6px 12px', border: '1px solid #dee2e6', maxWidth: '200px', wordWrap: 'break-word', textAlign: 'left', fontSize: '0.85rem', lineHeight: '1.2' }}>
                {item.notes}
              </td>
              <td style={{ padding: '6px 12px', border: '1px solid #dee2e6', textAlign: 'right', fontSize: '0.85rem', lineHeight: '1.2' }}>{formatDate(item.recDate)}</td>
              <td style={{ padding: '6px 12px', border: '1px solid #dee2e6', textAlign: 'right', fontSize: '0.85rem', lineHeight: '1.2' }}>{formatDate(item.expDate)}</td>
              <td style={{ padding: '6px 12px', border: '1px solid #dee2e6', textAlign: 'right', fontSize: '0.85rem', lineHeight: '1.2' }}>
                {item.PPU ? Number(item.PPU) : ''}
              </td>
              <td style={{ padding: '6px 12px', border: '1px solid #dee2e6', textAlign: 'right', fontSize: '0.85rem', lineHeight: '1.2' }}>{Number(item.onHand)}</td>
              <td style={{ padding: '6px 12px', border: '1px solid #dee2e6', textAlign: 'right', fontSize: '0.85rem', lineHeight: '1.2' }}>{renderAvailableQty(item)}</td>
              <td style={{ padding: '6px 12px', border: '1px solid #dee2e6', textAlign: 'center', fontSize: '0.85rem', lineHeight: '1.2' }}>
                                  <Button 
                    variant="outline-primary" 
                    size="sm" 
                    className="table-action-btn"
                    onClick={() => handleAddToCart(item)}
                    disabled={getCartItemQuantity(item.idLevels) >= Number(item.availQty)}
                    style={{ 
                      padding: '2px 8px',
                      fontSize: '0.75rem',
                      lineHeight: '1.2',
                      height: 'auto'
                    }}
                  >
                    <FontAwesomeIcon icon={faPlus} size="xs" />
                    <FontAwesomeIcon icon={faShoppingCart} size="xs" className="ms-1" />
                  </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' });
  };

  const renderAvailableQty = (item) => {
    if (!item) return null;
    const trueAvailable = getTrueAvailableQty(item);
    const cartQty = getCartItemQuantity(item.idLevels);
    
    return (
      <div className="available-qty" style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '4px' }}>
        {cartQty > 0 && (
          <span className="text-muted" style={{ fontSize: '0.75em' }}>
            ({cartQty} in cart)
          </span>
        )}
        <span style={{ fontSize: '0.85rem', lineHeight: '1.2' }}>{trueAvailable}</span>
      </div>
    );
  };

  return (
    <div className="inventory-container">
      <PageHeader 
        title="Inventory Management" 
        icon={faBoxes}
        subtitle="Track and manage your inventory levels"
      />
      <Container fluid className="inventory-container">
        <Row className="mb-3 align-items-center">
          <Col md={8}>
            <InputGroup>
              <InputGroup.Text>Search</InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Search inventory..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>
          </Col>
          <Col md={4} className="text-end d-flex justify-content-end align-items-center gap-2">
            <Button 
              variant="success"
              onClick={handleExportToExcel}
              className="export-btn"
            >
              <FontAwesomeIcon icon={faFileExcel} className="me-2" />
              Export to Excel
            </Button>
            <Button variant="outline-primary" onClick={() => setShowCart(true)}>
              <FontAwesomeIcon icon={faShoppingCart} />
              <Badge bg="secondary" className="ms-2">{cart.reduce((total, item) => total + item.quantity, 0)}</Badge>
            </Button>
          </Col>
        </Row>
        <div style={{ 
          height: 'calc(100vh - 250px)', 
          overflow: 'auto',
          border: '1px solid #e9ecef',
          borderRadius: '8px',
          backgroundColor: 'white'
        }}>
          {renderInventoryTable()}
          {filteredInventory.length === 0 && (
            <div style={{ 
              textAlign: 'center', 
              padding: '40px', 
              color: '#6c757d', 
              fontStyle: 'italic' 
            }}>
              No inventory items found matching your search.
            </div>
          )}
        </div>
        
        {/* Quantity Modal */}
        <Modal show={showQuantityModal} onHide={() => setShowQuantityModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Add to Cart</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedItem ? (
              <>
                <p>How many {selectedItem.itemName} would you like to add?</p>
                <Row className="align-items-center">
                  <Col xs={6}>
                    <Form.Control
                      type="number"
                      value={quantityToAdd}
                      onChange={(e) => setQuantityToAdd(Math.max(1, Math.min(Number(e.target.value), getTrueAvailableQty(selectedItem))))}
                      min="1"
                      max={getTrueAvailableQty(selectedItem)}
                      style={{ width: '100px' }}
                    />
                  </Col>
                  <Col xs={6}>
                    <span>Available: {getTrueAvailableQty(selectedItem)}</span>
                  </Col>
                </Row>
              </>
            ) : (
              <p>No item selected</p>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowQuantityModal(false)}>
              Cancel
            </Button>
            {selectedItem && (
              <>
                <Button variant="primary" onClick={handleQuantitySubmit}>
                  Add to Cart
                </Button>
                <Button variant="success" onClick={handleAddAll}>
                  Add All ({getTrueAvailableQty(selectedItem)})
                </Button>
              </>
            )}
          </Modal.Footer>
        </Modal>

        <Modal show={showCart} onHide={() => setShowCart(false)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Shopping Cart</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <ShoppingCart cart={cart} setCart={setCart} />
          </Modal.Body>
          <Modal.Footer>
            {cart.length > 0 && ( // Only show the button if cart has items
              <Button variant="primary" onClick={() => {
                setShowCart(false);
                setShowCheckout(true);
              }}>
                Proceed to Shipping
              </Button>
            )}
          </Modal.Footer>
        </Modal>

        <div style={{ position: 'fixed', bottom: 20, right: 20, zIndex: 1000 }}>
          <Toast 
            show={showToast} 
            onClose={() => setShowToast(false)} 
            delay={3000} 
            autohide
            style={{ backgroundColor: '#28a745', color: 'white' }}
          >
            <Toast.Body className="d-flex align-items-center">
              <FontAwesomeIcon icon={faCheck} className="me-2" />
              {toastMessage}
            </Toast.Body>
          </Toast>
        </div>
      </Container>
    </div>
  );
}

export default Inventory;
